/* eslint-disable no-console */
import { FC, useEffect, useId, useMemo, useState } from 'react';
import { useSSE } from 'hooks/useSSE';
import { API_BASE_URL, API_VERSION } from 'constants/common';
import { getNotifications, selectCurrentTeam, selectUserDetails, showNotifier } from 'containers/app/appSlice';
import { JobSSEEvents } from 'containers/jobs/constant';
import { VoidFn } from 'types/baseTypes';
import { useAppDispatch, useAppSelector } from 'hooks';
import { NotifierTypes } from 'constants/notifierConstants';
import { registerNotifierCallback, unregisterNotifierCallback } from 'containers/notifier-stack/utils';

interface JobDetailsSyncProps {
  jobId: string;
  onJobNoteUpdated: VoidFn;
  onJobReload: VoidFn;
}

interface JobSSEEvent {
  type: string;
  data: unknown;
  time: string;
  job_id: string;
  merchant_id: string;
}

export const JobDetailsSync: FC<JobDetailsSyncProps> = ({ jobId, onJobNoteUpdated, onJobReload }) => {
  const uniqueInstanceId = useId();
  const dispatch = useAppDispatch();
  const { id: currentTeamId } = useAppSelector(selectCurrentTeam);
  const { id: userId } = useAppSelector(selectUserDetails);
  const notifications = useAppSelector(getNotifications);

  // Track pending updates for hidden tabs
  const [hasPendingUpdates, setHasPendingUpdates] = useState(false);

  const uniqueClientId = useMemo(() => {
    return `${currentTeamId}${uniqueInstanceId.replace(/:/g, '_')}${Date.now()}`;
  }, [currentTeamId]);

  const { data: jobSSEData, error } = useSSE({
    // eslint-disable-next-line max-len
    url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/events/${currentTeamId}/jobs/${jobId}?client_id=${uniqueClientId}`
  });

  const NOTIFIER_RELOAD_JOB_KEY = `RELOAD_JOB_${jobId}`;

  useEffect(() => {
    registerNotifierCallback(NOTIFIER_RELOAD_JOB_KEY, onJobReload);
    return () => unregisterNotifierCallback(NOTIFIER_RELOAD_JOB_KEY);
  }, [onJobReload, jobId]);

  //log error
  useEffect(() => {
    if (error) {
      console.error('sse error:', error.message);
    }
  }, [error]);

  // page visibility - Show queued notifications when user returns
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && hasPendingUpdates) {
        handleShowNotifications();
        setHasPendingUpdates(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [hasPendingUpdates]);

  const handleShowNotifications = () => {
    const currentNotification = notifications.find(notification => notification.id === jobId);
    if (currentNotification) {
      return;
    }
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Job details have been updated',
          actionMessage: 'Reload',
          actionKey: NOTIFIER_RELOAD_JOB_KEY
        },
        id: jobId,
        type: NotifierTypes.INFO,
        showClose: true,
        shouldAnimate: true,
        duration: 15000
      })
    );
  };

  useEffect(() => {
    if (jobSSEData) {
      const jobSSEEvent = jobSSEData as JobSSEEvent;
      if (jobSSEEvent.type === JobSSEEvents.JOB_NOTE_UPDATED) {
        onJobNoteUpdated();
        return;
      }

      if (jobSSEEvent.type === JobSSEEvents.JOB_UPDATED && jobSSEEvent?.job_id === jobId) {
        if (userId !== jobSSEEvent?.merchant_id) {
          // different user made the change
          if (document.hidden) {
            setHasPendingUpdates(true);
          } else {
            handleShowNotifications();
          }
        } else {
          // same user made the change
          if (document.hidden) {
            setHasPendingUpdates(true);
          } else {
            // Tab is visible - no notification (user can see the changes directly)
          }
        }
      }
    }
  }, [jobSSEData, userId]);
  return <></>;
};
